import { useState, useEffect, useMemo, useRef, memo } from 'react'
import { createPortal } from 'react-dom'
import ColorPicker from './ColorPicker'
import useFitToScreen from './hooks/useFitToScreen'
import { formatNumber } from '../utils/chartUtils'
import { getWalletStats, getChartWallet, selectChartWallet, onChartWalletChange } from './hooks/useChartData'
import { openWalletMap } from '../utils/walletSources'
import { WalletMapIcon } from './WalletMapPanel'
import { getTank } from '../utils/meteoraTank'

/**
 * WalletStatsPanel — who is actually moving the charted token.
 *
 * Reads the mint-keyed wallet-stats registry in useChartData, folded at
 * INGEST (live + backfill + gap-fill), so the window is the whole replayed
 * session — not the 350-trade tape. Keyed by MINT rather than per chart
 * slot: with four slots running, a per-slot ref meant this panel could read
 * an empty Map while another slot held every wallet. Poll-based at 1Hz —
 * the registry is module state, so refreshing costs the app zero renders.
 *
 * ─── The visualization ───
 * A table of numbers makes you read every row to find the story, and the
 * story is almost always shape rather than digits: is one wallet eating
 * this, or is it broad — and who is distributing into the move.
 *
 *   DIVERGING BARS. Zero is the center line, green grows right (net buyer),
 *   red grows left (net seller), width scaled to the largest |net| on
 *   screen. One glance says balanced, one-sided, or single whale against a
 *   crowd — the same read the footprint gives on price. Numbers stay on the
 *   row for when you want them.
 *
 *   SUMMARY STRIP: net flow of the window, buyer/seller split, and TOP5
 *   concentration — the share of GROSS buying the five biggest hold. Gross,
 *   not net, because a whale who bought 100 and sold 90 still absorbed 100
 *   worth of supply. Concentration is what separates "accumulation" from
 *   "one guy", a distinction the row list alone hides.
 *
 * No scrollbar chrome: the list is ranked so both extremes are on screen
 * without scrolling. Scrolling still works for the tail.
 */

const short = (w) => `${w.slice(0, 4)}…${w.slice(-4)}`
const ago = (ts) => {
  const s = Math.max(0, (Date.now() - ts) / 1000)
  if (s < 60) return `${Math.floor(s)}s`
  if (s < 3600) return `${Math.floor(s / 60)}m`
  return `${Math.floor(s / 3600)}h`
}
const fmtSol = (n) => {
  const a = Math.abs(n)
  if (a >= 1000) return `${(n / 1000).toFixed(1)}K`
  if (a >= 100) return n.toFixed(0)
  if (a >= 10) return n.toFixed(1)
  return n.toFixed(2)
}

// Views, not sorts: each answers a different question about the tape.
const VIEWS = {
  flow:  { label: 'FLOW',  fn: (a, b) => Math.abs(b.net) - Math.abs(a.net) },   // both extremes
  accum: { label: 'ACCUM', fn: (a, b) => b.net - a.net },
  dist:  { label: 'DIST',  fn: (a, b) => a.net - b.net },
  vol:   { label: 'VOL',   fn: (a, b) => (b.buySol + b.sellSol) - (a.buySol + a.sellSol) },
  fresh: { label: 'NEW',   fn: (a, b) => b.first - a.first },                   // freshest wallets
  // WORK — buy PRINT COUNT, not size. "Buys into sells and keeps firing" is
  // a statement about repetition, and repetition is invisible to every other
  // view here: one 50 SOL buy and ten 5 SOL buys are the same row under VOL
  // and the same bar under FLOW. A wallet working an order in pieces only
  // separates from a wallet that hit once when you count the prints.
  // Deliberately a raw count with no size floor — a farm doing 400 dust buys
  // WILL top this list, and that is the correct answer to "who is firing
  // repeatedly", not a bug. The net bar and NET column next to it say which
  // kind you are looking at.
  work:  { label: 'WORK',  fn: (a, b) => (b.buys - a.buys) || (b.buySol - a.buySol) },
}

// ─── Panel colours (right-click menu) ───
// Overrides only: a key that is absent follows the app theme. Applied as CSS
// variables on .wstats-panel, so they cascade to every child rule that
// already reads var(--up) / var(--text-hi) / … and stay scoped to this
// panel — the rest of the app keeps the theme. Local-only for now, like the
// Smoketrail colours (settingsSync carries watchlists, not colours).
const TRACKER_COLORS_KEY = 'solcheddar:trackerColors'
const TRACKER_COLORS_EVENT = 'solcheddar:trackerColors'   // same-tab sync between panel instances
const COLOR_FIELDS = [
  { k: 'up',       css: '--up',              label: 'Buy',        fb: '#00cc6a', tip: 'Buyers: bars, net, BID' },
  { k: 'down',     css: '--down',            label: 'Sell',       fb: '#ff4455', tip: 'Sellers: bars, net, ASK' },
  { k: 'text',     css: '--text-hi',         label: 'Text',       fb: '#e6e8ec', tip: 'Wallets, values, title' },
  { k: 'muted',    css: '--text-lo',         label: 'Labels',     fb: '#565a63', tip: 'Captions, rank, N, column heads' },
  { k: 'bg',       css: '--surface',         label: 'Background', fb: '#12121a', tip: 'Panel background' },
  { k: 'seam',     css: '--seam',            label: 'Lines',      fb: '#1e2029', tip: 'Dividers and grid lines' },
  { k: 'selected', css: '--wstats-selected', label: 'Selected',   fb: '#c8cfdc', tip: 'Row shown on the chart' },
  { k: 'watched',  css: '--wstats-watched',  label: 'Watched',    fb: '#ffaa00', tip: 'Wallets on your watchlist' },
  { k: 'own',      css: '--wstats-own',      label: 'You',        fb: '#00ff88', tip: 'Your own wallet' },
]
const readTrackerColors = () => {
  try {
    const v = JSON.parse(localStorage.getItem(TRACKER_COLORS_KEY))
    return v && typeof v === 'object' ? v : {}
  } catch { return {} }
}
// Computed CSS colours come back as rgb()/rgba() or short hex; the picker
// only speaks #rrggbb.
const toHex6 = (c) => {
  if (!c) return null
  c = c.trim()
  let m = /^#([0-9a-f]{6})$/i.exec(c)
  if (m) return `#${m[1].toLowerCase()}`
  m = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/i.exec(c)
  if (m) return `#${m[1]}${m[1]}${m[2]}${m[2]}${m[3]}${m[3]}`.toLowerCase()
  m = /^rgba?\(\s*(\d+)[\s,]+(\d+)[\s,]+(\d+)/i.exec(c)
  if (m) return '#' + [m[1], m[2], m[3]].map(n => Math.min(255, +n).toString(16).padStart(2, '0')).join('')
  return null
}

// Minutes-to-empty, worded for glancing. Null = not draining.
const fmtEta = (m) => {
  if (m == null) return null
  if (m < 1) return '<1m'
  if (m < 60) return `${Math.round(m)}m`
  if (m < 24 * 60) return `${(m / 60).toFixed(1)}h`
  return `${Math.round(m / 1440)}d`
}

function WalletStatsPanel({ data, dataSource, watchedWallets = [], publicKey, isCme = false }) {
  const [view, setView] = useState('flow')
  const [tick, setTick] = useState(0)
  const [copied, setCopied] = useState(null)
  // Mirror of the module-level chart selection, so the highlighted row and
  // the badges on the chart can never disagree — one source, two readers.
  const [chartWallet, setChartWallet] = useState(() => getChartWallet())
  useEffect(() => onChartWalletChange(setChartWallet), [])
  // How many rows are clickable for the chart overlay. The buffer stores
  // ids for every wallet, so this is a UI choice, not a storage one.
  const SELECTABLE = 20
  const copyTimer = useRef(null)

  // ─── Colours + right-click menu ───
  const panelRef = useRef(null)
  const menuRef = useRef(null)
  const [colors, setColorsState] = useState(readTrackerColors)
  const [colorMenu, setColorMenu] = useState(null)   // {x, y, base} | null
  useFitToScreen(menuRef, !!colorMenu, colorMenu?.x ?? 0, colorMenu?.y ?? 0)
  const setColors = (next) => {
    try {
      if (next && Object.keys(next).length) localStorage.setItem(TRACKER_COLORS_KEY, JSON.stringify(next))
      else localStorage.removeItem(TRACKER_COLORS_KEY)
    } catch {}
    setColorsState(next || {})
    try { window.dispatchEvent(new CustomEvent(TRACKER_COLORS_EVENT)) } catch {}
  }
  useEffect(() => {
    const sync = () => setColorsState(readTrackerColors())
    const onStorage = (e) => { if (e.key === TRACKER_COLORS_KEY) sync() }
    window.addEventListener(TRACKER_COLORS_EVENT, sync)
    window.addEventListener('storage', onStorage)
    return () => {
      window.removeEventListener(TRACKER_COLORS_EVENT, sync)
      window.removeEventListener('storage', onStorage)
    }
  }, [])
  const panelStyle = useMemo(() => {
    const st = {}
    for (const f of COLOR_FIELDS) if (colors[f.k]) st[f.css] = colors[f.k]
    return st
  }, [colors])
  const openColorMenu = (e) => {
    e.preventDefault()
    e.stopPropagation()
    // Theme values as they resolve on the panel BEFORE our overrides, so an
    // untouched swatch shows what is actually drawn and Reset matches it.
    const base = {}
    const host = panelRef.current?.parentElement || document.documentElement
    const cs = getComputedStyle(host)
    for (const f of COLOR_FIELDS) base[f.k] = toHex6(cs.getPropertyValue(f.css)) || f.fb
    setColorMenu({ x: e.clientX, y: e.clientY, base })
  }
  useEffect(() => {
    if (!colorMenu) return
    const close = () => setColorMenu(null)
    // Right-click on the panel again re-opens at the new spot instead of
    // racing this listener closed.
    const onCtx = (e) => { if (!panelRef.current?.contains(e.target)) close() }
    const onKey = (e) => { if (e.key === 'Escape') close() }
    const t = setTimeout(() => {
      window.addEventListener('mousedown', close)
      window.addEventListener('contextmenu', onCtx)
      window.addEventListener('keydown', onKey)
    }, 0)
    return () => {
      clearTimeout(t)
      window.removeEventListener('mousedown', close)
      window.removeEventListener('contextmenu', onCtx)
      window.removeEventListener('keydown', onKey)
    }
  }, [colorMenu])

  // Styled like the Smoketrail menu in FootprintChartCanvas.
  const colorMenuEl = colorMenu && createPortal(
    <div
      ref={menuRef}
      onMouseDown={(e) => e.stopPropagation()}
      onContextMenu={(e) => { e.preventDefault(); e.stopPropagation() }}
      style={{
        position: 'fixed', left: colorMenu.x, top: colorMenu.y, zIndex: 1000,
        background: '#16181c', border: '1px solid #2a2d33', borderRadius: 6,
        boxShadow: '0 6px 20px rgba(0,0,0,0.5)', padding: 4, minWidth: 160,
        fontFamily: "'JetBrains Mono', monospace",
      }}
    >
      <div style={{ fontSize: 8, fontWeight: 700, color: '#555', letterSpacing: '0.06em', textTransform: 'uppercase', padding: '4px 8px 2px' }}>
        Tracker Colors
      </div>
      {COLOR_FIELDS.map((f) => (
        <div key={f.k} title={f.tip}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
                   padding: '4px 8px', fontSize: 11, borderRadius: 4, color: colors[f.k] ? '#fff' : '#bbb' }}>
          <span>{f.label}</span>
          <ColorPicker
            value={colors[f.k] || colorMenu.base[f.k] || f.fb}
            onChange={(c) => setColors({ ...readTrackerColors(), [f.k]: c })}
            title={f.tip} />
        </div>
      ))}
      <div
        onClick={() => { setColors({}); setColorMenu(null) }}
        title="Follow the app theme again"
        style={{ padding: '6px 8px', fontSize: 11, borderRadius: 4, cursor: 'pointer', color: '#bbb',
                 borderTop: '1px solid #2a2d33', marginTop: 4 }}
        onMouseEnter={(e) => { e.currentTarget.style.background = '#1c1f24' }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent' }}
      >
        Reset colors
      </div>
    </div>,
    document.body
  )

  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 1000)
    return () => clearInterval(id)
  }, [])
  useEffect(() => () => clearTimeout(copyTimer.current), [])

  // Pool fuel gauge — published by FPCC's Meteora hook, read by mint. Same
  // 1Hz tick as the wallet fold, so one interval drives the whole panel.
  const tank = useMemo(() => getTank(data.currentToken),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tick, data.currentToken])

  const { rows, maxAbs, summary } = useMemo(() => {
    const M = getWalletStats(data.currentToken)
    if (!M || M.size === 0) return { rows: [], maxAbs: 1, summary: null }
    const all = []
    let netAll = 0, buyers = 0, sellers = 0, grossBuy = 0, roundTrip = 0
    for (const [wallet, w] of M) {
      const net = w.buySol - w.sellSol
      netAll += net
      grossBuy += w.buySol
      // Round-tripped SOL: the part of a wallet's buying it handed straight
      // back. min(buy, sell) is the overlap regardless of which side led, so
      // it reads the same for buy-then-sell and sell-then-buy.
      roundTrip += Math.min(w.buySol, w.sellSol)
      if (net > 0) buyers++
      else if (net < 0) sellers++
      all.push({ wallet, ...w, net })
    }
    const topBuyers = [...all].sort((a, b) => b.buySol - a.buySol).slice(0, 5)
    const top5 = topBuyers.reduce((s, x) => s + x.buySol, 0)
    const topPrints = topBuyers.reduce((s, x) => s + x.buys, 0)
    const topAvg = topPrints > 0 ? top5 / topPrints : 0
    all.sort(VIEWS[view].fn)
    const shown = all.slice(0, 60)
    // Scale on GROSS-on-the-net-side, not |net|. Normalising to net made a
    // wallet that bought 100 and sold 90 render as a stub indistinguishable
    // from one that bought 10 and held — same bar, opposite behaviour. The
    // track now measures how much they moved; the fill measures how much
    // they kept.
    let mx = 0
    for (const r of shown) {
      const g = r.net >= 0 ? r.buySol : r.sellSol
      if (g > mx) mx = g
    }
    return {
      rows: shown,
      maxAbs: mx || 1,
      summary: {
        wallets: M.size, netAll, buyers, sellers,
        conc:  grossBuy > 0 ? top5 / grossBuy : 0,
        churn: grossBuy > 0 ? roundTrip / grossBuy : 0,
        topPrints, topAvg,
      },
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick, view, data.currentToken])

  const copy = async (wallet) => {
    try {
      await navigator.clipboard.writeText(wallet)
      setCopied(wallet)
      clearTimeout(copyTimer.current)
      copyTimer.current = setTimeout(() => setCopied(null), 1200)
    } catch { /* clipboard blocked — title still shows the address */ }
  }

  if (dataSource === 'hyperliquid') {
    return (
      <div className="wstats-panel" ref={panelRef} style={panelStyle} onContextMenu={openColorMenu}>
        <div className="wstats-header"><span className="wstats-title">Tracker</span></div>
        <div className="wstats-empty">{isCme ? 'CME trades are anonymous - no wallets to track. Switch to a Solana token.' : 'Hyperliquid fills are venue-anonymous — no per-wallet identity to fold. Switch to a Solana token.'}</div>
        {colorMenuEl}
      </div>
    )
  }

  return (
    <div className="wstats-panel" ref={panelRef} style={panelStyle} onContextMenu={openColorMenu}>
      {colorMenuEl}
      <div className="wstats-header">
        <span className="wstats-title">Tracker</span>
        {chartWallet && (
          <button className="wstats-view active" style={{ marginLeft: 6 }}
                  title="Clear the wallet highlighted on the chart (Esc)"
                  onClick={() => selectChartWallet(null)}>
            {short(chartWallet)} ×
          </button>
        )}
        <span className="wstats-views">
          {Object.entries(VIEWS).map(([k, v]) => (
            <button key={k} className={`wstats-view${view === k ? ' active' : ''}`}
              onClick={() => setView(k)}>{v.label}</button>
          ))}
        </span>
      </div>

        {/* Five cells forced onto one line. The class was laid out for four
            and the fifth wrapped. Overridden here rather than in styles.css
            so the change stays in one file, and it works whether the rule
            underneath is flex or a 4-column grid — gap and padding still
            come from the class, only the track count is taken over.
            minmax(0, …) is load-bearing: a bare 1fr floors at min-content,
            so the widest value would push the row wide again instead of
            letting the cells share the space that exists. Belongs in
            styles.css eventually.

            The TOP5 and CHURN mini-bars are gone: at five across, a bar 60px
            wide cannot resolve a percentage the digits state exactly, and it
            cost a row of height on two cells while the other three had none.
            Removing them also makes every cell label-plus-value and nothing
            else, so the row self-aligns without an alignItems override. */}
      {summary && (
        <div className="wstats-summary" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
        }}>
          <div className="wsum-cell">
            <div className="wsum-k">NET FLOW</div>
            <div className={`wsum-v ${summary.netAll >= 0 ? 'up' : 'down'}`}>
              {summary.netAll >= 0 ? '+' : ''}{fmtSol(summary.netAll)} ◎
            </div>
          </div>
          <div className="wsum-cell">
            <div className="wsum-k">BUY / SELL</div>
            <div className="wsum-v">
              <span className="up">{formatNumber(summary.buyers)}</span>
              <span className="wsum-sep">/</span>
              <span className="down">{formatNumber(summary.sellers)}</span>
            </div>
          </div>
          <div className="wsum-cell">
            <div className="wsum-k">WALLETS</div>
            <div className="wsum-v">{formatNumber(summary.wallets)}</div>
          </div>
          <div className="wsum-cell" title={`Share of gross buying held by the 5 largest buyers. High = one hand; low = broad participation.\nTop 5 fired ${summary.topPrints} buy prints, averaging ${fmtSol(summary.topAvg)} ◎ each — many small prints is an order being worked, few large ones is a single hit.`}>
            <div className="wsum-k">TOP5</div>
            <div className="wsum-v">{Math.round(summary.conc * 100)}%</div>
          </div>
          {/* CHURN exists to disambiguate TOP5, which is not a signal on its
              own: concentrated buying looks identical whether one hand is
              accumulating or a farm is trading with itself. This is the share
              of gross buying that was handed straight back as selling — near
              zero means the size stayed bought, high means it round-tripped.
              High TOP5 + low CHURN is accumulation. High TOP5 + high CHURN is
              wash. Same first number, opposite conclusion.
              No threshold is asserted: the number is shown, the colour only
              marks direction, and where the line sits is for the tape to say
              once there are failures to compare against. */}
          <div className="wsum-cell" title={`Share of gross buying handed back as selling — round-trip volume as a fraction of all buying.\nRead WITH Top5: concentrated + low churn = one hand accumulating; concentrated + high churn = wallets trading with themselves.\n0% = everything bought stayed bought.`}>
            <div className="wsum-k">CHURN</div>
            <div className={`wsum-v ${summary.churn > 0.5 ? 'down' : ''}`}>{Math.round(summary.churn * 100)}%</div>
          </div>
        </div>
      )}

      {tank && (() => {
        // ─── POOL LIQ: two bars, two facts ───
        // 1. TUG — bid vs ask share of pool depth. One 100%-stacked bar with
        //    a fixed center tick: divider right of the tick = bid-heavy.
        //    Green right = what the pool can PAY sellers (support); red left
        //    = token overhang waiting to be sold into strength.
        // 2. MAGNITUDE — total pool USD on a FIXED log scale ($1k → $10M),
        //    so a thin pool reads thin on every coin instead of the bar
        //    renormalizing per-token. Color keys off absolute size.
        // Everything else the old gauge printed (exact amounts, drain fit,
        // ETA, floor) lives in the tooltip — read on demand, not at a glance.
        const both = tank.bidUsd != null && tank.askUsd != null && (tank.bidUsd + tank.askUsd) > 0
        const bidShare = both ? tank.bidUsd / (tank.bidUsd + tank.askUsd) : null
        const total = tank.totalUsd ?? tank.usd
        const magPct = total > 0
          ? Math.max(2, Math.min(100, ((Math.log10(total) - 3) / 4) * 100))
          : 0
        const magClass = total < 10_000 ? ' low' : total < 100_000 ? ' mid' : ''
        const fmtUsd = (n) => {
          if (n >= 1e6) return `$${(n / 1e6).toFixed(2)}M`
          if (n >= 1e3) return `$${(n / 1e3).toFixed(1)}K`
          return `$${n.toFixed(0)}`
        }
        const tip = [
          both ? `bid ${fmtUsd(tank.bidUsd)} · ask ${fmtUsd(tank.askUsd)} · total ${fmtUsd(total)}`
               : `bid ${fmtUsd(total)} (bid-only feed — server predates ask side)`,
          `bid tank ${fmtSol(tank.tank)} ${tank.sym} (peak ${fmtSol(tank.peak)})`,
          tank.drainPerMin > 0.001
            ? `draining −${tank.drainPerMin.toFixed(1)} ${tank.sym}/min${tank.etaMin != null ? ` · ~${fmtEta(tank.etaMin)} to empty` : ''}`
            : 'bid steady over the last 10m',
          tank.floorPct > 0 ? `floor −${Math.round(tank.floorPct * 100)}% (fetched bid bins exhausted)` : null,
          tank.partial ? 'PARTIAL: liquidity continues past the fetched window — true depth is larger' : null,
          'Meteora DLMM · 1 pool · all bins · not slippage-adjusted',
        ].filter(Boolean).join('\n')
        return (
          <div className="wtank" title={tip}>
            <div className="wtank-head">
              <span className="wtank-title">POOL LIQ</span>
              {tank.partial && <span className="wtank-flag">PARTIAL</span>}
              {tank.drainPerMin > 0.001 && <span className="wtank-drain down">draining</span>}
            </div>

            {both && (
              <div className="wtank-row">
                <span className="wtank-lbl down">ASK</span>
                <div className="wtank-tug">
                  <div className="wtank-ask" style={{ transform: `scaleX(${1 - bidShare})` }} />
                  <div className="wtank-bid" style={{ transform: `scaleX(${bidShare})` }} />
                  <div className="wtank-mid" />
                </div>
                <span className="wtank-lbl up">BID</span>
              </div>
            )}

            <div className="wtank-row">
              <span className="wtank-lbl">LIQ</span>
              <div className="wtank-mag">
                <div className={`wtank-mag-fill${magClass}`} style={{ transform: `scaleX(${magPct / 100})` }} />
              </div>
              <span className="wtank-val">{fmtUsd(total)}</span>
            </div>
          </div>
        )
      })()}

      <div className="wstats-cols">
        <span className="wc-rank">#</span>
        <span>WALLET</span>
        <span className="wc-flow">◂ SELL · FLOW · BUY ▸</span>
        <span className="wc-net">NET ◎</span>
        <span className="wc-n">N</span>
      </div>

      <div className="wstats-list">
        {rows.length === 0 && (
          <div className="wstats-empty">No wallet activity folded yet — stats build as trades stream in.</div>
        )}
        {rows.map((r, i) => {
          // ─── Gross track, net fill, one tick per print ───
          // Three facts, all already on the row, none stored:
          //   LENGTH  gross traded on the side this wallet nets to
          //   FILL    |net| as a share of that gross — solid is what stuck,
          //           hollow is what was handed straight back
          //   TICKS   print count on that side; each cell is therefore the
          //           AVERAGE print size, which is a real number
          //           (buySol/buys). It does NOT claim every print was equal
          //           — the distribution is not stored and is not drawn.
          //           Many narrow cells = an order worked in pieces; one wide
          //           cell = a single hit. That contrast is the whole point.
          const gross = r.net >= 0 ? r.buySol : r.sellSol
          const prints = r.net >= 0 ? r.buys : r.sells
          const frac = gross / maxAbs
          // Share of the track that stayed bought. gross - |net| is exactly
          // the round-tripped SOL, so hollow is not a rendering choice, it is
          // the churn drawn at 1:1.
          const solid = gross > 0 ? Math.min(1, Math.abs(r.net) / gross) : 0
          // 40 is a DRAW cap, not a data cap. Past it the pitch goes
          // sub-pixel and the ticks alias into a smear; dense-but-legible
          // reads as "many small prints", which is the honest summary. The
          // true count stays in the tooltip and in the N column.
          const segs = Math.max(1, Math.min(40, prints))
          const pitch = 100 / segs
          // White at low alpha so ticks read on BOTH regions — dark on the
          // hollow would vanish against the panel, colour on the fill would
          // vanish against itself.
          const ticks = segs > 1
            ? `repeating-linear-gradient(90deg, transparent 0 calc(${pitch}% - 1px), rgba(255,255,255,0.32) calc(${pitch}% - 1px) ${pitch}%)`
            : 'none'
          const col = r.net >= 0 ? 'var(--up, #22c55e)' : 'var(--down, #ef4444)'
          // A fully round-tripped wallet has net 0 and would draw nothing at
          // all — and those are precisely the farm wallets worth seeing.
          const trackW = Math.max(gross > 0 ? 3 : 0, frac * 100)
          const isOwn = publicKey && r.wallet === publicKey
          const watched = watchedWallets.find(w => r.wallet === w.address)
          const pos = r.net >= 0
          return (
            <div
              key={r.wallet}
              className={`wstats-row${isOwn ? ' own' : ''}${watched ? ' watched' : ''}${chartWallet === r.wallet ? ' selected' : ''}${i < SELECTABLE ? ' selectable' : ''}`}
              // Click = show this wallet's prints on the chart (toggle).
              // Double-click = copy the address. Copy used to be the single
              // click; it moved because selection is the action you take
              // many times in a row and copy is the one you take once.
              onClick={() => { if (i < SELECTABLE) selectChartWallet(chartWallet === r.wallet ? null : r.wallet) }}
              onDoubleClick={(e) => { e.preventDefault(); copy(r.wallet) }}
              title={`${r.wallet}\nbuys ${r.buys} · ${fmtSol(r.buySol)} ◎${r.buys ? ` · avg ${fmtSol(r.buySol / r.buys)} ◎/print` : ''}\nsells ${r.sells} · ${fmtSol(r.sellSol)} ◎${r.sells ? ` · avg ${fmtSol(r.sellSol / r.sells)} ◎/print` : ''}\nbar: ${fmtSol(gross)} ◎ moved, ${fmtSol(Math.abs(r.net))} kept (${Math.round((1 - solid) * 100)}% round-tripped) over ${prints} print${prints === 1 ? '' : 's'}${prints > 40 ? ' — ticks capped at 40' : ''}\nfirst seen ${ago(r.first)} ago · last ${ago(r.last)} ago\n${i < SELECTABLE ? (chartWallet === r.wallet ? 'click to clear from chart' : 'click to show on chart') + ' · ' : ''}double-click to copy`}
            >
              <span className="wr-rank">{String(i + 1).padStart(2, '0')}</span>
              <span className="wr-name wr-name-map">
                {/* Whole box opens the wallet map. It swallows click/double-click
                    so the row's select-on-chart / copy don't also fire; those
                    still work everywhere else on the row. */}
                <span className="wmap-namebox is-btn" role="button" tabIndex={0}
                  title="Open wallet map" aria-label="Open wallet map"
                  onClick={(e) => { e.stopPropagation(); openWalletMap(r.wallet) }}
                  onDoubleClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => { if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); e.stopPropagation(); openWalletMap(r.wallet) } }}>
                <span className="wr-name-text">{copied === r.wallet ? 'COPIED' : (watched?.label || short(r.wallet))}</span>
                <span className="wmap-open-btn" aria-hidden="true"><WalletMapIcon size={12} /></span>
                </span>
                {isOwn && <span className="wr-you">YOU</span>}
              </span>

              {/* Diverging bar: center = 0, right = net buyer, left = net seller */}
              <span className="wr-bar">
                <span className="wr-half left">
                  {!pos && (
                    // marginLeft:auto — the left half grows leftward FROM the
                    // axis, so the track is right-aligned and the solid part
                    // sits against the centre line where zero is.
                    <span style={{
                      display: 'block', height: '100%', width: `${trackW}%`, marginLeft: 'auto',
                      border: `1px solid ${col}`, borderRadius: 1, boxSizing: 'border-box',
                      backgroundImage: ticks,
                    }}>
                      <span style={{ display: 'block', height: '100%', marginLeft: 'auto',
                                     width: `${solid * 100}%`, background: col }} />
                    </span>
                  )}
                </span>
                <span className="wr-axis" />
                <span className="wr-half right">
                  {pos && (
                    <span style={{
                      display: 'block', height: '100%', width: `${trackW}%`,
                      border: `1px solid ${col}`, borderRadius: 1, boxSizing: 'border-box',
                      backgroundImage: ticks,
                    }}>
                      <span style={{ display: 'block', height: '100%',
                                     width: `${solid * 100}%`, background: col }} />
                    </span>
                  )}
                </span>
              </span>

              <span className={`wr-net ${pos ? 'up' : 'down'}`}>
                {pos ? '+' : ''}{fmtSol(r.net)}
              </span>
              <span className="wr-n">{r.buys + r.sells}</span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// (perf3) The panel reads exactly one thing from `data`: currentToken. Its
// rows / tank are folded from module stores on its OWN 1Hz tick, so an App
// commit (every data tick hands it a fresh `data` object) changed nothing it
// shows — yet re-rendered up to 60 rows each time. Re-render only when a prop
// it actually reads changes; its own state (tick, view, copied, chartWallet)
// still re-renders it as before.
export default memo(WalletStatsPanel, (a, b) => (
  a.data?.currentToken === b.data?.currentToken
  && a.dataSource === b.dataSource
  && a.watchedWallets === b.watchedWallets
  && a.publicKey === b.publicKey
))
